'use client';

import { AccordionImageWrapperProps } from '@/components/accordion-image-wrapper/types';
import { HeadlineWrapper } from '@/components/headline-wrapper/HeadlineWrapper';
import RenderImageText from '@/components/image-text';
import clsx from 'clsx';
import ChipsWrapper from '@/components/chips/ChipsWrapper';
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/button/Button';
import { useTranslations } from '@/lib';

const prioritizedOrder = ['privatkunden', 'firmenkunden'];

export default function AccordionImageWrapper({
  headlineWrapper,
  accordionImage,
  sectionId,
}: AccordionImageWrapperProps) {
  const { t } = useTranslations();

  const uniqueTabs = useMemo(() => {
    const tabs = Array.from(
      new Set(
        accordionImage
          .map((accordion) => accordion.tabs)
          .filter(Boolean) as string[],
      ),
    );

    return tabs.sort((a, b) => {
      const aIndex = prioritizedOrder.indexOf(a.toLowerCase());
      const bIndex = prioritizedOrder.indexOf(b.toLowerCase());
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }, [accordionImage]);

  const [selectedTab, setSelectedTab] = useState<string | null>(null);
  const resolvedSelectedTab = useMemo(
    () =>
      uniqueTabs.length === 0
        ? null
        : selectedTab && uniqueTabs.includes(selectedTab)
          ? selectedTab
          : uniqueTabs[0],
    [selectedTab, uniqueTabs],
  );

  const uniqueTags = useMemo(() => {
    const tags = Array.from(
      new Set(
        accordionImage
          .filter(
            (accordion) =>
              !resolvedSelectedTab || accordion.tabs === resolvedSelectedTab,
          )
          .map((accordion) => accordion.tag)
          .filter(Boolean) as string[],
      ),
    );
    return tags.sort((a, b) => a?.localeCompare(b));
  }, [accordionImage, resolvedSelectedTab]);

  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const resolvedSelectedTag = useMemo(
    () =>
      uniqueTags.length === 0
        ? null
        : selectedTag && uniqueTags.includes(selectedTag)
          ? selectedTag
          : uniqueTags[0],
    [selectedTag, uniqueTags],
  );

  const filteredAccordion = useMemo(
    () =>
      accordionImage.filter(
        (accordion) =>
          (!resolvedSelectedTab || accordion.tabs === resolvedSelectedTab) &&
          (!resolvedSelectedTag || accordion.tag === resolvedSelectedTag),
      ),
    [accordionImage, resolvedSelectedTab, resolvedSelectedTag],
  );
  const activeAccordion = filteredAccordion[0] ?? null;

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    setSelectedTag(null);
  };

  const handleTagChange = (tags: string[]) => {
    setSelectedTag(tags[0] ?? null);
  };

  return (
    <div
      id={sectionId}
      className="accordion-image-wrapper relative py-10 lg:py-20"
    >
      <div className="mx-auto flex flex-col gap-6 lg:gap-12">
        {headlineWrapper && (
          <HeadlineWrapper {...headlineWrapper} className="!py-0" />
        )}
        <div className="flex flex-col gap-6">
          {uniqueTabs.length > 0 && (
            <div
              className={clsx(
                'flex gap-4',
                headlineWrapper?.position === 'center' ? 'mx-auto' : '',
              )}
            >
              {uniqueTabs.map((tab) => (
                <Button
                  key={tab}
                  variant={'tertiary'}
                  type="button"
                  name={t(`accordionImageWrapper.tabs.${tab}`)}
                  onClick={() => handleTabChange(tab)}
                  className={clsx(
                    'text-base capitalize',
                    resolvedSelectedTab === tab
                      ? 'font-medium !text-brand-700 !underline !underline-offset-4'
                      : '!text-neutral-500',
                  )}
                />
              ))}
            </div>
          )}
          {uniqueTags.length > 0 && (
            <div
              className={clsx(
                'flex flex-col gap-6',
                headlineWrapper?.position === 'center' ? 'lg:mx-auto' : '',
              )}
            >
              <ChipsWrapper
                key={resolvedSelectedTab ?? 'all'}
                options={uniqueTags}
                selectedOptions={
                  resolvedSelectedTag ? [resolvedSelectedTag] : []
                }
                onSelectionChange={handleTagChange}
                variant="inline-wrap"
                selectionMode="single"
              />
            </div>
          )}
        </div>
        {activeAccordion && (
          <div className="flex flex-col gap-6">
            <div>
              <div className="accordion-active">
                <RenderImageText {...activeAccordion} variant="image" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
